import { useEffect, useState } from "react";
import { Flex } from "@mantine/core";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router";
import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import SaveIcon from "../components/common/Icons/SaveIcon";
import AreYouSureModal from "../components/common/Modals/AreYouSureModal";
import TableDeleteButton from "../components/common/Buttons/TableDeleteButton";
import RotationForm, {
  RotationFormValues,
  emptyRotationForm,
  formFromRotation,
  payloadOf,
} from "../components/Rotation/RotationForm";
import {
  RotationConflict,
  createShiftRotation,
  deleteShiftRotation,
  getShiftRotationById,
  updateShiftRotation,
} from "../api/shiftRotationApi";
import { createNotification } from "../utils/useNotification";
import { formatDayKey, todayKey } from "../utils/shiftPattern";

/**
 * ROTA OLUŞTUR / DÜZENLE (plan 2.34).
 *
 * Aynı sayfa iki iş yapıyor; ayırt eden `:id` parametresi (`"new"` = oluştur).
 * `WorkShiftDetail` / `NewWorkShift` ikilisinden farklı olarak tek dosya:
 * rota formunun tamamı `RotationForm`'da ve iki sayfa yalnızca kaydetme
 * çağrısında ayrışıyordu — iki kopya bakım yükü olurdu.
 */
const ShiftRotationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";

  const [form, setForm] = useState<RotationFormValues>(emptyRotationForm());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [conflicts, setConflicts] = useState<RotationConflict[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [startedAlready, setStartedAlready] = useState(false);

  useEffect(() => {
    if (isNew) return;
    getShiftRotationById(id!)
      .then((data: any) => {
        if (!data?.response) return;
        setForm(formFromRotation(data.response));
        // Yürürlüğe girmiş rota silinemez; buton buna göre gizleniyor ki
        // İK backend'den reddedilen bir işlemi denemek zorunda kalmasın.
        setStartedAlready(data.response.startDate <= todayKey());
      })
      .catch(() => createNotification({ success: false, message: "Rota yüklenemedi." }))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const submit = async () => {
    if (!form.name.trim()) {
      createNotification({ success: false, message: "Rota adı gerekli." });
      return;
    }
    if (!form.departmentId) {
      createNotification({ success: false, message: "Bir departman seçin." });
      return;
    }
    const payload = payloadOf(form);
    if (payload.teamIds.length === 0) {
      createNotification({ success: false, message: "Rotada en az bir ekip olmalı." });
      return;
    }
    if (payload.shiftDefinitionIds.every((x) => x == null)) {
      createNotification({
        success: false,
        message: "Çevrimin tamamı dinlenme olamaz.",
      });
      return;
    }

    setSaving(true);
    setConflicts([]);
    try {
      const response: any = isNew
        ? await createShiftRotation(payload)
        : await updateShiftRotation(id!, {
            name: payload.name,
            teamIds: payload.teamIds,
            shiftDefinitionIds: payload.shiftDefinitionIds,
            periodType: payload.periodType,
            periodCount: payload.periodCount,
            anchorDate: payload.anchorDate,
            startDate: payload.startDate,
            endDate: payload.endDate,
          });

      if (response?.success) {
        createNotification({
          success: true,
          message: isNew ? "Rota oluşturuldu." : "Rota güncellendi.",
        });
        navigate("/shifts/rotations");
        return;
      }

      // 409: aynı departmanda tarihleri çakışan rota var. Doğru çözüm eskisini
      // KAPATMAK — silmek geçmiş puantajı değiştirirdi, backend zaten
      // reddediyor. Bu yüzden hata mesajı değil, çakışan rotalar gösteriliyor.
      if (response?.conflicts?.length) {
        setConflicts(response.conflicts);
        return;
      }

      createNotification({
        success: false,
        message: response?.message ?? "Rota kaydedilemedi.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const response: any = await deleteShiftRotation(id!);
    createNotification({
      success: response?.success,
      message: response?.success ? "Rota silindi." : response?.message,
    });
    if (response?.success) navigate("/shifts/rotations");
  };

  if (loading) return null;

  return (
    <Container>
      <PageHeader
        pageTitle={isNew ? "Yeni Rota" : "Rotayı Düzenle"}
        subText="Ekipleri ve çevrimdeki vardiyaları sıralayın; kimin hangi dönemde nerede olduğu aşağıda hesaplanır."
        isButtoned
        buttonText="Kaydet"
        buttonIcon={<SaveIcon size={24} />}
        isButtonDisabled={saving}
        buttonOnClick={submit}
      />

      {conflicts.length > 0 && (
        <ConflictBox>
          <ConflictTitle>
            Bu departmanda seçilen tarihlerde zaten bir rota var
          </ConflictTitle>
          <ConflictText>
            İki rota aynı günü kapsayamaz — o günün vardiyası belirsiz kalırdı.
            Aşağıdaki rotaya bir <strong>bitiş tarihi</strong> verip kapatın,
            sonra bu rotayı kaydedin. Eskisini silmeyin: geçmiş puantaj
            değişmemeli.
          </ConflictText>
          {conflicts.map((conflict) => (
            <ConflictRow key={conflict.rotationId}>
              <span>{conflict.name}</span>
              <span>
                {formatDayKey(conflict.startDate)} –{" "}
                {conflict.endDate ? formatDayKey(conflict.endDate) : "süresiz"}
              </span>
              <ConflictLink
                onClick={() => navigate(`/shifts/rotations/${conflict.rotationId}`)}
              >
                Aç
              </ConflictLink>
            </ConflictRow>
          ))}
        </ConflictBox>
      )}

      <RotationForm value={form} onChange={setForm} departmentLocked={!isNew} />

      {!isNew && (
        <Footer>
          {startedAlready ? (
            <FooterNote>
              Bu rota yürürlüğe girdiği için <strong>silinemez</strong>. Sona
              erdirmek için "Geçerlilik Bitişi" verin — silmek o ekipteki
              herkesin geçmiş günlerini ölçütsüz bırakırdı.
            </FooterNote>
          ) : (
            <Flex align="center" gap={10}>
              <FooterNote>Bu rota henüz başlamadı, silinebilir.</FooterNote>
              <TableDeleteButton onClick={() => setDeleteOpen(true)} />
            </Flex>
          )}
        </Footer>
      )}

      <AreYouSureModal
        opened={deleteOpen}
        setOpened={setDeleteOpen}
        proccessFunc={handleDelete}
        title="Rotayı Sil"
        text={`"${form.name}" rotasını silmek istediğinizden emin misiniz?`}
        confirmButtonText="Sil"
        cancelButtonText="Vazgeç"
      />
    </Container>
  );
};

export default ShiftRotationDetail;

const ConflictBox = styled.div`
  border: 1px solid #fca5a5;
  background: #fef2f2;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 28px;
`;

const ConflictTitle = styled.div`
  color: #b91c1c;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const ConflictText = styled.div`
  color: #7f1d1d;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 12px;
`;

const ConflictRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: #7f1d1d;
  font-size: 14px;
  padding: 6px 0;
  border-top: 1px solid #fecaca;
`;

const ConflictLink = styled.button`
  background: none;
  border: none;
  color: #b91c1c;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

const Footer = styled.div`
  margin-top: 36px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const FooterNote = styled.div`
  color: #6b7280;
  font-size: 13px;
  line-height: 20px;
  max-width: 640px;
`;
